// Omnix/client/ui/TicketPanel.js
import { ComponentBase } from "../core/ComponentBase.js";

export class TicketPanel extends ComponentBase {
    static styles = `
        .ticket-panel-content {
            /* Styles for the panel content */
        }
        .ticket-summary-grid {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 1rem;
            font-size: 0.9rem;
        }
        .summary-item .form-label { /* Target labels inside summary items */
            font-weight: 500;
            color: var(--bs-secondary-color);
            margin-bottom: 0.25rem;
            font-size: 0.8rem;
        }
        .summary-item .form-select-sm {
            font-size: 0.9rem;
        }

        .ticket-panel-content .sidebar-section-title {
            font-weight: 600;
            font-size: 0.9rem;
            color: var(--bs-secondary-color);
            text-transform: uppercase;
            margin-bottom: 0.75rem;
            margin-top: 1rem;
        }
        .ticket-panel-content .list-group-item {
            background-color: var(--bs-tertiary-bg);
            border-color: var(--bs-border-color-translucent);
        }
        .ticket-panel-content .list-group-item.active {
            /* More subtle active state */
            background-color: var(--bs-primary-bg-subtle) !important;
            color: var(--bs-primary-text-emphasis) !important;
            border-left: 3px solid var(--bs-primary);
            padding-left: calc(1rem - 3px);
        }
        .ticket-panel-content .list-group-item .badge {
            background-color: var(--bs-secondary-bg-subtle) !important;
            color: var(--bs-secondary-text-emphasis) !important;
        }
        .ticket-panel-content .list-group-item.active .badge {
            background-color: var(--bs-primary) !important;
            color: var(--bs-primary-contrast) !important;
        }

    `;

    constructor(config) {
        super(config);
        this.conversation = null;
        this.ticketData = null;
        this.assignableAgents = [];
        this.linkedTickets = []; // Initialize as empty array

        this.applyStyles();
    }

    async initialize() {
        await super.initialize();
    }

    async initAfterLogin() {
        // Fetch assignable agents once, as they are needed for the dropdowns
        try {
            this.assignableAgents = await this.appClient.rpcClient.call('UnifiedInboxRpc.getAssignableAgents', {});
        } catch (error) {
            this.log.error("Failed to fetch assignable agents for TicketPanel:", error);
        }
    }

    async setConversation(conversation) {
        this.conversation = conversation;
        await this.loadTicketData();
    }

    async loadTicketData() {
        this.ticketData = null; // This will now hold only the primary ticket for the summary view
        this.linkedTickets = []; // This will hold ALL linked tickets for the list view

        if (this.conversation && Array.isArray(this.conversation.associatedTicketIds) && this.conversation.associatedTicketIds.length > 0) {
            try {
                // Use a new, efficient RPC to get all linked tickets in one call
                const result = await this.appClient.rpcClient.call('TicketRpc.findByIds', {
                    _ids: this.conversation.associatedTicketIds
                });

                this.linkedTickets = result.data || [];

                // The primary ticket for the summary is the most recently linked one
                if (this.linkedTickets.length > 0) {
                    this.ticketData = this.linkedTickets[this.linkedTickets.length - 1];
                }

            } catch (error) {
                this.log.error(`Failed to load associated ticket data for conversation ${this.conversation._id}:`, error);
            }
        }
        // Always re-render after attempting to load data.
        if (this.container) this.render();
    }



    render() {
        if (!this.container) {
            this.container = document.createElement('div');
            this.container.className = 'ticket-panel-content';
        }
        this.container.innerHTML = '';

        if (!this.conversation) {
            return this.container;
        }

        if (this.ticketData) {
            this.renderTicketSummary();
        }

        // 2. Render the list of all linked tickets if there's more than one
        if (this.linkedTickets.length > 0) {
            this.renderTicketList();
        }

        // 3. Always render the action buttons
        this.renderTicketActions();

        return this.container;
    }

    renderTicketActions() {
        const div = document.createElement('div');
        div.className = 'mt-3'; // Add margin top

        // --- START OF FIX: Conditional message rendering ---
        if (this.linkedTickets.length === 0) {
            div.innerHTML += `<p class="text-muted small mb-2 text-center">${this.t('This conversation is not linked to a ticket.')}</p>`;
        }
        // --- END OF FIX ---

        div.innerHTML += `
            <div class="d-grid gap-2">
                <button class="btn btn-primary btn-sm" data-action="create">${this.t('Create New Ticket')}</button>
                <button class="btn btn-secondary btn-sm" data-action="link">${this.t('Link to Existing Ticket')}</button>
            </div>
        `;
        div.addEventListener('click', (e) => {
            const action = e.target.closest('button')?.dataset.action;
            if (action === 'create') {
                this.handleCreateTicket();
            } else if (action === 'link') {
                const unifiedInbox = this.appClient.layoutEngine.componentManager.componentInstances['mainInbox'];
                if (unifiedInbox && unifiedInbox.linkTicketModal) {
                    unifiedInbox.linkTicketModal.show(this.conversation);
                }
            }
        });
        this.container.appendChild(div);
    }

    renderTicketSummary() { // This method is now focused only on the summary part
        const statusOptions = ['new', 'open', 'pending', 'solved', 'closed'].map(s => `<option value="${s}" ${this.ticketData.status === s ? 'selected' : ''}>${this.t(s.charAt(0).toUpperCase() + s.slice(1))}</option>`).join('');
        const priorityOptions = ['low', 'normal', 'high', 'urgent'].map(p => `<option value="${p}" ${this.ticketData.priority === p ? 'selected' : ''}>${this.t(p.charAt(0).toUpperCase() + p.slice(1))}</option>`).join('');
        const assigneeOptions = this.assignableAgents.map(a => `<option value="${a.value}" ${this.ticketData.assigneeId === a.value ? 'selected' : ''}>${this.t(a.label)}</option>`).join('');

        const div = document.createElement('div');
        div.innerHTML = `
            <div class="d-flex justify-content-between align-items-center mb-3">
                <h6 class="mb-0">${this.t('Primary Ticket')} #${this.ticketData.ticketNumber}</h6>
                <a href="/user/tickets?ticketId=${this.ticketData._id}" class="btn btn-sm btn-outline-secondary" title="${this.t('View Full Details')}"><i class="bi bi-box-arrow-up-right"></i></a>
            </div>
            <div class="ticket-summary-grid">
                <div class="summary-item">
                    <label class="form-label">${this.t('Status')}</label>
                    <select class="form-select form-select-sm" data-field="status">${statusOptions}</select>
                </div>
                <div class="summary-item">
                    <label class="form-label">${this.t('Priority')}</label>
                    <select class="form-select form-select-sm" data-field="priority">${priorityOptions}</select>
                </div>
            </div>
            <div class="summary-item mt-3">
                <label class="form-label">${this.t('Assignee')}</label>
                <select class="form-select form-select-sm" data-field="assigneeId">
                    <option value="">-- ${this.t('Unassigned')} --</option>
                    ${assigneeOptions}
                </select>
            </div>
        `;

        div.querySelectorAll('select').forEach(select => {
            select.addEventListener('change', (e) => {
                const field = e.target.dataset.field;
                const value = e.target.value || null;
                this.handleTicketUpdate({ [field]: value });
            });
        });

        this.container.appendChild(div);
    }

    renderTicketList() {
        const listContainer = document.createElement('div');
        listContainer.className = 'mt-3';
        const title = this.linkedTickets.length > 1 ? 'All Linked Tickets' : 'Linked Ticket';
        listContainer.innerHTML = `<h6 class="sidebar-section-title">${this.t(title)}</h6>`;

        const listGroup = document.createElement('div');
        listGroup.className = 'list-group list-group-flush';

        this.linkedTickets.forEach(ticket => {
            const isPrimary = ticket._id === this.ticketData._id;
            const item = document.createElement('a');
            item.href = `/user/tickets?ticketId=${ticket._id}`;
            item.className = `list-group-item list-group-item-action p-2 d-flex justify-content-between align-items-center ${isPrimary ? 'active' : ''}`;
            item.innerHTML = `
                <div class="small">
                    <strong class="d-block">#${ticket.ticketNumber}</strong>
                    <span class="text-muted">${ticket.subject.substring(0, 30)}...</span>
                </div>
                <span class="badge rounded-pill bg-secondary">${this.t(ticket.status)}</span>
            `;
            listGroup.appendChild(item);
        });

        listContainer.appendChild(listGroup);
        this.container.appendChild(listContainer);
    }

    async handleCreateTicket() {
        if (!this.conversation) return;

        const unifiedInbox = this.appClient.layoutEngine.componentManager.componentInstances['mainInbox'];
        if (unifiedInbox && unifiedInbox.ticketForm) {
            const assignableAgents = await this.appClient.rpcClient.call('UnifiedInboxRpc.getAssignableAgents', {});
            const initialData = {
                contactId: this.conversation.contactId,
                conversationId: this.conversation._id,
                subject: `Ticket from conversation: ${this.conversation.lastInteractionSnippet || ''}`.substring(0, 100),
                status: 'new',
                priority: 'normal'
            };
            unifiedInbox.ticketForm.show('create', initialData, assignableAgents);
        }
    }


    async handleTicketUpdate(updates) {
        try {
            await this.appClient.rpcClient.call('TicketRpc.update', {
                _id: this.ticketData._id,
                ...updates
            });
            this.appClient.notifier.success("Ticket updated.");
            // No need to reload data, the server event will trigger a refresh via the parent.
        } catch (error) {
            this.log.error("Failed to update ticket from sidebar:", error);
            this.appClient.notifier.error("Could not update ticket.");
            // Re-render to revert the dropdown to its original state on failure
            this.render();
        }
    }
}